import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Tag, Plus, Trash2, Hash } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useData } from '../context/DataContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const COLORS = [
  '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', 
  '#3B82F6', '#EF4444', '#6366F1', '#14B8A6'
];

const TagsPage = () => {
  const { tags, createTag, deleteTag } = useData();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newTag, setNewTag] = useState({ name: '', color: '#8B5CF6' });
  const [isLoading, setIsLoading] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTag.name.trim()) return;
    
    setIsLoading(true);
    try {
      await createTag(newTag);
      setNewTag({ name: '', color: '#8B5CF6' });
      setIsDialogOpen(false);
      toast.success('Tag created');
    } catch (err) {
      toast.error('Failed to create tag');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (e, tagId) => {
    e.stopPropagation();
    if (window.confirm('Delete this tag?')) {
      try {
        await deleteTag(tagId);
        toast.success('Tag deleted');
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  return (
    <div className="min-h-screen" data-testid="tags-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Tag className="w-7 h-7 text-violet-400" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">Tags</h1>
          </div>
          <p className="text-zinc-500">Categorize your ads with tags</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-violet-600 hover:bg-violet-500" data-testid="add-tag-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Tag
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Add Tag</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-400">Name</Label>
                <Input
                  data-testid="tag-name-input"
                  value={newTag.name}
                  onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                  placeholder="Tag name"
                  className="mt-1.5 bg-zinc-800 border-zinc-700 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-400">Color</Label>
                <div className="flex gap-2 mt-2">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewTag({ ...newTag, color })}
                      className={`w-8 h-8 rounded-full transition-transform ${
                        newTag.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-zinc-900 scale-110' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-violet-600 hover:bg-violet-500"
                data-testid="create-tag-btn"
              >
                {isLoading ? 'Creating...' : 'Create Tag'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tags List */}
      {tags.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-full bg-zinc-800/50 flex items-center justify-center mx-auto mb-4">
            <Hash className="w-10 h-10 text-zinc-600" />
          </div>
          <h3 className="text-xl font-semibold text-zinc-300 mb-2">No tags yet</h3>
          <p className="text-zinc-500 mb-6">Create tags to categorize your ads</p>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="bg-violet-600 hover:bg-violet-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Tag
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {tags.map((tag, index) => (
            <motion.div
              key={tag.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.03 }}
            >
              <Badge
                className="
                  group relative px-4 py-2 text-base cursor-pointer
                  bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700
                  transition-all duration-200
                "
                onClick={() => navigate(`/?tag=${tag.id}`)}
                data-testid={`tag-badge-${tag.id}`}
              >
                <span
                  className="w-2 h-2 rounded-full mr-2"
                  style={{ backgroundColor: tag.color }}
                />
                <span className="text-zinc-200">{tag.name}</span>
                <button
                  onClick={(e) => handleDelete(e, tag.id)}
                  className="
                    ml-2 opacity-0 group-hover:opacity-100
                    text-zinc-500 hover:text-red-400 transition-opacity
                  "
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </Badge>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagsPage;
